/** @jsxImportSource @emotion/react */
import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { TheWordGame } from "@packages/sdk";

import {
  constantSemanticColors,
  fontWeights,
  radius,
  spacing,
} from "../../../../../global/emotion";
import type { CardProps } from "../../../refactor/Card/Card.emotion";
import { Card } from "../../../refactor/Card/Card.emotion";
import { calculateWon } from "./calculateWon";
import { TheWordScore } from "./TheWordScore.emotion";

const cardStyle: Interpolation = {
  paddingBottom: "96px",
  paddingTop: `calc(${spacing.full} - ${spacing.xs})`,
};

const imgContainerStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  alignItems: "center",
  backgroundColor: props.color,
  display: "flex",
  justifyContent: "center",
});
const successNumberStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  background: constantSemanticColors.onColorHighest,
  borderRadius: radius.full,
  color: props.color,
  display: "inline-flex",
  fontWeight: fontWeights.buttonM,
  height: spacing.l,
  placeContent: "center",
  placeItems: "center",
  width: spacing.l,
});
const infoContainerStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const maskStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const theWordScoreStyle: Interpolation = {
  paddingBottom: spacing.m,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};

export type TheWordMediumProps = CardProps &
  Pick<TheWordGame, "summary"> & { won?: boolean };

export const TheWordMedium = ({
  centerActions,
  color,
  description,
  imgContainerCSS,
  imgSrc,
  infoContainerCSS,
  maskProps,
  summary,
  title,
  won,
  ...props
}: TheWordMediumProps) => {
  const t = useTranslations();

  won = calculateWon({ won, matrix: summary?.matrix });

  return (
    <Card
      centerActions={summary ? null : centerActions}
      color={color}
      description={
        (description ?? won)
          ? t.rich("the_word_successful_explanation", {
              "0": summary?.matrix?.length ?? 0,
              b: (chunks) => (
                <span css={successNumberStyle({ color })}>{chunks}</span>
              ),
            })
          : summary
            ? t("the_word_failed_description")
            : t("the_word_card_description")
      }
      imgContainerProps={{
        children: summary ? (
          <TheWordScore
            color={color}
            css={theWordScoreStyle}
            summary={summary}
          />
        ) : null,
      }}
      imgContainerCSS={[imgContainerStyle({ color }), imgContainerCSS]}
      imgSrc={summary ? null : imgSrc}
      infoContainerCSS={[infoContainerStyle, infoContainerCSS]}
      maskContainerCSS={maskStyle}
      maskProps={maskProps}
      rightActions={summary ? centerActions : null}
      css={cardStyle}
      title={
        (title ?? summary) ? t("the_word_finished_title") : t("the_word_title")
      }
      {...props}
    />
  );
};
